import { createContext, useContext, useState, useEffect, useRef } from "react";
import api from "../../services/APIService";
import { getUserInfo } from "../../utils/AuthManager";

// 建立 Shopping Context（購物功能用）
const ShoppingContext = createContext();
const getStorageKey = () => {
  const pathPrefix = window.location.pathname.split("/")[1] || "default";
  return `${pathPrefix}_shoppingItems_${localStorage.getItem(
    "shopping_userid",
  )}`;
};

export const ShoppingProvider = ({ children }) => {
  const [shoppingItems, setShoppingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const storageKey = getStorageKey();

  //商品預覽視窗
  const [disableHover, setDisableHover] = useState(false);

  // 從資料庫載入購物車
  const loadCartFromDatabase = async (memberNo) => {
    try {
      const result =
        await api.productOverviewApi.productOverviewGetCartItemsGet({
          memberNo: memberNo,
        });

      if (result.success && result.data) {
        return result.data.map((item) => {
          // 計算折扣資訊
          const originalPrice = Number(item.OriginalPrice) || 0;
          const price = Number(item.Price) || 0;
          const hasDiscount = item.HasDiscount === "Y" || originalPrice > price;
          const discountPercent =
            hasDiscount && originalPrice > 0
              ? Math.floor(((originalPrice - price) / originalPrice) * 100)
              : 0;
          const productDiscount = hasDiscount ? originalPrice - price : 0;

          // 組裝優惠券資訊（如果有）
          const appliedCoupon = item.PromotionsDiscountNo
            ? {
                Id: item.PromotionsDiscountNo,
                DiscountCode: item.CouponCode || "",
                DiscountWay: item.DiscountWay || "",
                DiscountValue: item.DiscountValue || null,
                DiscountName: item.CouponName || "",
                MinQuantity: item.MinQuantity || 0,
                FullAmount: item.FullAmount || null,
              }
            : null;

          return {
            cartItemNo: item.CartItemNo,
            productNo: item.ProductNo,
            name: item.Name,
            price: price,
            originalPrice: originalPrice,
            color: item.Color,
            colorNo: item.ColorNo,
            size: item.Size,
            specNo: item.SpecNo,
            quantity: item.Quantity,
            imageUrl: item.Image,
            promotionsDiscountNo: item.PromotionsDiscountNo,

            // 商品折扣資訊
            hasDiscount: hasDiscount,
            discountPercent: discountPercent,
            productDiscount: productDiscount,

            // 優惠券資訊
            appliedCoupon: appliedCoupon,
          };
        });
      }
      return [];
    } catch (error) {
      console.error("從資料庫載入購物車失敗:", error);
      return [];
    }
  };

  const addToShopping = async (item) => {
    if (!item.color || !item.size) {
      alert("請選擇顏色與尺寸");
      return;
    }

    // 取得會員資訊
    const userInfo = getUserInfo();

    // 取得 API 返回的 cartItemNo
    let cartItemNo = null;

    // 如果有會員登入，同步呼叫 API
    if (userInfo && userInfo.memberNo) {
      try {
        const result =
          await api.productOverviewApi.productOverviewAddToCartPost({
            addToCartRequest: {
              memberNo: userInfo.memberNo,
              materialNo: item.productNo,
              materialColorNo: item.colorNo,
              sizeSpecificationNo: item.specNo,
              quantity: item.quantity,
              promotionsDiscountNo: item.appliedCoupon?.Id ?? null,
            },
          });
        if (result.success) {
          // 取得返回的 cartItemNo
          cartItemNo = result.data?.cartItemNo;
        } else {
          console.warn("會員加入購物車失敗:", result.message);
        }
      } catch (error) {
        console.error("加入購物車 API 呼叫失敗:", error);
        // API 失敗不影響前端 localStorage 的操作
      }
    }

    // 維持原本的 localStorage 邏輯（無論是否有會員都執行）
    setShoppingItems((prev) => {
      const exist = prev.find(
        (p) =>
          p.productNo === item.productNo &&
          p.color === item.color &&
          p.size === item.size,
      );
      if (exist) {
        return prev.map((p) =>
          p.productNo === item.productNo &&
          p.color === item.color &&
          p.colorNo === item.colorNo &&
          p.size === item.size &&
          p.specNo === item.specNo
            ? {
                ...p,
                quantity: p.quantity + item.quantity,
                cartItemNo: cartItemNo || p.cartItemNo, // 更新或保留 cartItemNo
              }
            : p,
        );
      }
      return [...prev, { ...item, cartItemNo }]; // 新增時加入 cartItemNo
    });
  };

  const removeFromShopping = async (productNo, color, size) => {
    // 取得會員資訊
    const userInfo = getUserInfo();

    // 如果有會員登入，找到對應的購物車項目並呼叫 API
    if (userInfo && userInfo.memberNo) {
      const item = shoppingItems.find(
        (p) =>
          p.productNo === productNo && p.color === color && p.size === size,
      );

      if (item && item.cartItemNo) {
        try {
          const result =
            await api.productOverviewApi.productOverviewRemoveCartItemDelete({
              cartItemNo: item.cartItemNo,
            });

          if (!result.success) {
            console.warn("資料庫移除失敗:", result.message);
          }
        } catch (error) {
          console.error("移除購物車 API 呼叫失敗:", error);
        }
      }
    }

    // 維持原本的 localStorage 邏輯
    setShoppingItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.productNo === productNo &&
            item.color === color &&
            item.size === size
          ),
      ),
    );
  };

  const clearShopping = async () => {
    // 取得會員資訊
    const userInfo = getUserInfo();

    // 如果有會員登入，呼叫 API 清空購物車
    if (userInfo && userInfo.memberNo) {
      try {
        const result =
          await api.productOverviewApi.productOverviewClearCartDelete({
            memberNo: userInfo.memberNo,
          });

        if (!result.success) {
          console.warn("資料庫清空失敗:", result.message);
        }
      } catch (error) {
        console.error("清空購物車 API 呼叫失敗:", error);
      }
    }

    // 清空 state
    setShoppingItems([]);

    // 明確清除 localStorage 中的購物車資料
    localStorage.removeItem(storageKey);
  };

  const clearShoppingLocalOnly = () => {
    setShoppingItems([]);
    // 清除所有購物車相關的 localStorage 資料
    localStorage.removeItem(storageKey);
  };

  // ==================== 檢查並移除購物車中過期的優惠券 ====================
  const validateCartCoupons = async () => {
    try {
      const itemsWithCoupons = shoppingItems.filter(
        (item) => item.appliedCoupon && item.appliedCoupon.Id,
      );

      if (itemsWithCoupons.length === 0) return;

      const userInfo = getUserInfo();
      if (!userInfo || !userInfo.memberNo) return;

      // 收集所有優惠券 ID
      const couponIds = itemsWithCoupons.map((item) => item.appliedCoupon.Id);

      // 呼叫批次檢查 API
      const result =
        await api.productOverviewApi.productOverviewCheckCouponExpiryPost({
          checkCouponExpiryRequest: {
            CouponIds: couponIds,
          },
        });

      if (!result.success || !result.data) {
        console.warn("批次檢查優惠券失敗，跳過自動移除");
        return;
      }

      // 建立過期優惠券的 Set（用於快速查找）
      const expiredCouponIds = new Set(
        result.data.ExpiredCoupons.map((c) => c.CouponId),
      );

      // 移除過期的優惠券
      const updatePromises = itemsWithCoupons.map(async (item) => {
        const isExpired = expiredCouponIds.has(item.appliedCoupon.Id);

        if (isExpired) {
          console.warn(
            `商品 ${item.name} 的優惠券 ${item.appliedCoupon.DiscountName} 已過期，將自動移除`,
          );

          // 更新購物車項目（移除優惠券）
          if (item.cartItemNo) {
            try {
              await api.productOverviewApi.productOverviewUpdateCartItemQuantityPut(
                {
                  updateCartItemRequest: {
                    cartItemNo: item.cartItemNo,
                    quantity: item.quantity,
                    promotionsDiscountNo: null, // 移除優惠券
                  },
                },
              );
            } catch (error) {
              console.error("更新購物車失敗:", error);
            }
          }

          return {
            ...item,
            appliedCoupon: null,
            promotionsDiscountNo: null,
          };
        }

        return item;
      });

      const updatedItems = await Promise.all(updatePromises);

      // 更新購物車狀態
      setShoppingItems((prev) =>
        prev.map((item) => {
          const updated = updatedItems.find(
            (u) =>
              u &&
              u.productNo === item.productNo &&
              u.colorNo === item.colorNo &&
              u.specNo === item.specNo,
          );
          return updated || item;
        }),
      );
    } catch (error) {
      console.error("驗證購物車優惠券失敗:", error);
    }
  };

  const updateQuantity = async (productNo, color, size, newQty) => {
    // 取得會員資訊
    const userInfo = getUserInfo();

    // 如果有會員登入，找到對應的購物車項目並呼叫 API
    if (userInfo && userInfo.memberNo) {
      const item = shoppingItems.find(
        (p) =>
          p.productNo === productNo && p.color === color && p.size === size,
      );

      if (item && item.cartItemNo) {
        try {
          const result =
            await api.productOverviewApi.productOverviewUpdateCartItemQuantityPut(
              {
                updateCartItemRequest: {
                  cartItemNo: item.cartItemNo,
                  quantity: newQty,
                },
              },
            );

          if (!result.success) {
            console.warn("資料庫更新數量失敗:", result.message);
          }
        } catch (error) {
          console.error("更新購物車數量 API 呼叫失敗:", error);
        }
      }
    }

    // 維持原本的 localStorage 邏輯
    setShoppingItems((prev) =>
      prev.map((item) =>
        item.productNo === productNo &&
        item.color === color &&
        item.size === size
          ? { ...item, quantity: newQty }
          : item,
      ),
    );
  };

  // 同分頁防止 StrictMode 重跑（不影響另開分頁/視窗）
  const initOnceRef = useRef(false);

  // 統一 item identity：productNo + colorNo + specNo
  const itemKey = (x) => `${x.productNo}_${x.colorNo}_${x.specNo}`;

  // 合併購物車：同 key 則去重，並保留 cartItemNo / appliedCoupon 等資料
  // sumQuantities=true：來源是「不同裝置/時間點」的資料，可相加（如 DB + 未同步的本機新增）
  // sumQuantities=false：來源是「同一使用者的不同 localStorage key」，取較大值，避免重複計算
  const mergeCartItems = (items, sumQuantities = true) => {
    const map = new Map();

    for (const it of items) {
      if (!it) continue;

      const k = itemKey(it);
      if (!map.has(k)) {
        map.set(k, { ...it });
        continue;
      }

      const prev = map.get(k);

      // 依 sumQuantities 決定數量處理方式
      const prevQty = Number(prev.quantity) || 0;
      const itQty = Number(it.quantity) || 0;
      const mergedQty = sumQuantities
        ? prevQty + itQty // 不同來源（DB + 本機未同步）→ 相加
        : Math.max(prevQty, itQty); // 同一使用者的不同 key → 取較大值，避免重複計算

      map.set(k, {
        ...prev,
        ...it,
        quantity: mergedQty,

        // cartItemNo：優先保留「有值的」
        cartItemNo: it.cartItemNo || prev.cartItemNo,

        // appliedCoupon / promotionsDiscountNo：優先保留「有值的」
        appliedCoupon: it.appliedCoupon || prev.appliedCoupon,
        promotionsDiscountNo:
          it.promotionsDiscountNo || prev.promotionsDiscountNo,
      });
    }

    return Array.from(map.values());
  };

  const initializeCart = async ({ force = false } = {}) => {
    console.log("初始化購物車...", { initOnce: initOnceRef.current, force });

    // 只有在非 force 時才擋 StrictMode 重跑
    if (!force && initOnceRef.current) return;
    initOnceRef.current = true;

    const userInfo = getUserInfo();
    const memberNo = userInfo?.memberNo || null;
    try {
      if (memberNo) {
        // 1) 先從 DB 載入（DB 是主）
        const dbCart = await loadCartFromDatabase(memberNo);

        // 如果是強制重新載入（force = true，登入後觸發），合併訪客購物車後同步到 DB
        if (force) {
          const pathPrefix =
            window.location.pathname.split("/")[1] || "default";
          const guestStorageKeyNull = `${pathPrefix}_shoppingItems_null`;

          // 讀取訪客購物車：
          // 1. 當前 storageKey（訪客期間用 UUID 或 null）
          // 2. _null key（舊版相容）
          // 兩者都讀再合併，避免漏資料
          const currentSaved = localStorage.getItem(storageKey);
          const nullSaved = localStorage.getItem(guestStorageKeyNull);
          const currentCart = currentSaved ? JSON.parse(currentSaved) : [];
          const nullCart = nullSaved ? JSON.parse(nullSaved) : [];
          const guestCart = mergeCartItems(
            [...currentCart, ...nullCart],
            false,
          );

          // 找出訪客有、DB 沒有的商品 → 同步到 DB
          const dbKeySet = new Set(dbCart.map(itemKey));
          const unsyncedItems = guestCart.filter(
            (item) => !dbKeySet.has(itemKey(item)),
          );

          const syncedItems = [];
          for (const item of unsyncedItems) {
            try {
              const result =
                await api.productOverviewApi.productOverviewAddToCartPost({
                  addToCartRequest: {
                    memberNo: memberNo,
                    materialNo: item.productNo,
                    materialColorNo: item.colorNo,
                    sizeSpecificationNo: item.specNo,
                    quantity: item.quantity,
                    promotionsDiscountNo: item.appliedCoupon?.Id ?? null,
                  },
                });
              if (result.success) {
                syncedItems.push({
                  ...item,
                  cartItemNo: result.data?.cartItemNo,
                });
              } else {
                syncedItems.push(item);
                console.warn(
                  `訪客商品 ${item.name || item.productNo} 同步失敗:`,
                  result.message,
                );
              }
            } catch (error) {
              syncedItems.push(item);
              console.error(
                `訪客商品 ${item.name || item.productNo} 同步失敗:`,
                error,
              );
            }
          }

          // 合併：DB + 從訪客同步的商品
          const mergedCart = mergeCartItems([...dbCart, ...syncedItems]);
          setShoppingItems(mergedCart);

          console.log(
            `購物車載入完成（登入後）：${dbCart.length} 筆來自資料庫，${syncedItems.length} 筆從訪客購物車同步，合併後共 ${mergedCart.length} 筆`,
          );

          // 清除訪客購物車資料（storageKey 和 _null key 都清）
          if (currentCart.length > 0) {
            localStorage.removeItem(storageKey);
          }
          if (nullCart.length > 0) {
            localStorage.removeItem(guestStorageKeyNull);
          }

          // 檢查並移除過期的優惠券
          setTimeout(() => {
            validateCartCoupons();
          }, 500);

          setIsLoading(false);
          return;
        }

        // 登入狀態：完全以 DB 為唯一資料來源，不合併 localStorage。
        // 原因：addToShopping / removeFromShopping 均即時同步 DB，
        // localStorage 只是快取；在多網址（localhost / test / ap）環境下，
        // 各 pathPrefix 的舊 localStorage 殘留資料若被當作「未同步」而寫回 DB，
        // 會導致已刪除的商品重新出現。
        setShoppingItems(dbCart);

        console.log(`購物車載入完成：${dbCart.length} 筆來自資料庫`);

        // 檢查並移除過期的優惠券
        setTimeout(() => {
          validateCartCoupons();
        }, 500);
      } else {
        // 沒會員：只從 localStorage 載入
        const saved = localStorage.getItem(storageKey);
        const localCart = saved ? JSON.parse(saved) : [];
        setShoppingItems(mergeCartItems(localCart));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 初始化購物車（每個分頁/視窗 mount 時會跑一次）
  useEffect(() => {
    // storageKey 變動（例如登入/登出換帳號），需重置 initOnceRef 才能重新初始化
    initOnceRef.current = false;
    initializeCart();
  }, [storageKey]); // 依 storageKey（路徑/使用者id變動）重新初始化

  // 同步到 localStorage（載入完成後才執行）
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem(storageKey, JSON.stringify(shoppingItems));
    }
  }, [shoppingItems, storageKey, isLoading]);

  return (
    <ShoppingContext.Provider
      value={{
        shoppingItems,
        addToShopping,
        removeFromShopping,
        clearShopping,
        clearShoppingLocalOnly,
        updateQuantity,
        disableHover,
        setDisableHover,
        isLoading,
        initializeCart,
        validateCartCoupons,
      }}
    >
      {children}
    </ShoppingContext.Provider>
  );
};

// 自定義 Hook，供子元件使用
export const useShoppingContext = () => {
  const context = useContext(ShoppingContext);
  if (!context) {
    throw new Error("useShoppingContext 必須在 ShoppingProvider 中使用");
  }
  return context;
};
